import type {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import {formatTherapyState} from './therapyFormatters'

// Filters of the "Terapie accessorie" sub-tab in the patient detail. Which filters are shown (and
// their labels) comes from patientDetailConfiguration.secondaryTherapiesConfiguration.filtersConfig,
// configured via the PSP configurator's "Dettaglio paziente > Terapia accessoria" tab. Keep the ids in
// sync with patientSecondaryTherapyFilterOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.
//
// The secondary-therapies endpoint returns the whole (small) list for a patient with no server-side
// filtering, so filters are applied client-side.

export interface SecondaryTherapyFilterConfig {
  id: string
  label?: string
}

export const SECONDARY_THERAPY_FILTER_IDS = {
  DRUG_NAME: 'drugName',
  STATE: 'state'
} as const

const SECONDARY_THERAPY_STATES = ['ACTIVE', 'NOT_ACTIVE']

// Transitional fallback to the legacy `drug.state` for documents that predate the root `state` field
// (RF18 of therapy-service specs/2026-09-notification-secondaryTherapy.md) and haven't been
// backfilled yet.
export function getSecondaryTherapyState(secondaryTherapy: Record<string, any>): string | undefined {
  return secondaryTherapy.state ?? secondaryTherapy.drug?.state
}

export function getSecondaryTherapyDrugName(secondaryTherapy: Record<string, any>): string {
  return secondaryTherapy.drug?.name ?? 'Terapia accessoria'
}

// Active therapies first (same idea as the primary therapies' tab strip, which opens on the active
// one); the relative order returned by the backend is otherwise preserved.
export function sortSecondaryTherapies(secondaryTherapies: Record<string, any>[]): Record<string, any>[] {
  const rank = (therapy: Record<string, any>) => getSecondaryTherapyState(therapy) === 'NOT_ACTIVE' ? 1 : 0
  return [...secondaryTherapies].sort((a, b) => rank(a) - rank(b))
}

export function buildSecondaryTherapyFilterItems(
  filtersConfig: SecondaryTherapyFilterConfig[],
  secondaryTherapies: Record<string, any>[]
): FilterItem[] {
  return filtersConfig.reduce<FilterItem[]>((items, {id, label}) => {
    if (id === SECONDARY_THERAPY_FILTER_IDS.DRUG_NAME) {
      const drugNames = Array.from(new Set(secondaryTherapies.map(therapy => therapy.drug?.name).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b))
      items.push({
        id,
        label: label || 'Farmaco',
        type: 'select',
        showSearch: true,
        options: drugNames.map(name => ({value: name, label: name}))
      })
    } else if (id === SECONDARY_THERAPY_FILTER_IDS.STATE) {
      items.push({
        id,
        label: label || 'Stato',
        type: 'select',
        options: SECONDARY_THERAPY_STATES.map(state => ({value: state, label: formatTherapyState(state)}))
      })
    }
    return items
  }, [])
}

export function applySecondaryTherapyFilters(
  secondaryTherapies: Record<string, any>[],
  filters: FilterResult[]
): Record<string, any>[] {
  return filters.reduce((filtered, {property, value}) => {
    if (property === SECONDARY_THERAPY_FILTER_IDS.DRUG_NAME) {
      return filtered.filter(therapy => therapy.drug?.name === value)
    }
    if (property === SECONDARY_THERAPY_FILTER_IDS.STATE) {
      return filtered.filter(therapy => getSecondaryTherapyState(therapy) === value)
    }
    return filtered
  }, secondaryTherapies)
}
